import React, { useCallback, useContext, useMemo } from 'react';
import { StudioContext, StudioSidePanelContext, useExpandBlock, UserContext } from 'venn-components';
import { Icon } from 'venn-ui-kit';
import { buttonize, PageType, useModuleLazily } from 'venn-utils';
import BlockItem, { LinkedBlockIcon, PageIconContinuationLine } from './components/BlockItem';
import ItemActions from './components/ItemActions';
import NewPageButton from './components/NewPageButton';
import { getDynamicIcon, getPageTitle } from '../../../logic/studioUtils';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import type { Layout, LayoutItem } from 'react-grid-layout';
import { Match } from 'effect';
import {
  blockDisplayHeader,
  type BlockId,
  blockExpandedRows,
  blockSettings,
  blockSourceLayoutState,
  continuationUserBlocksState,
  expandedBlockIdsState,
  insertBlockIntoPage,
  isReportState,
  makeContinuationLayoutId,
  type OutlineDropDestinationKind,
  packLayoutByReadingOrder,
  planOutlineExpandedDrop,
  RenderedPage,
  renderedPagesState,
  reorderAboveExpandedBlock,
  reorderBlockWithinPage,
  reorderContinuationUserBlocks,
  RL_GRID_LANDSCAPE_NO_COLUMNS,
  RL_GRID_LANDSCAPE_NO_ROWS,
  RL_GRID_PORTRAIT_NO_COLUMNS,
  RL_GRID_PORTRAIT_NO_ROWS,
  selectedBlockIdState,
  selectedBlocksState,
  sortLayoutByReadingOrder,
  studioMaxUsableRows,
  studioPrintOrientationType,
  viewPages,
} from 'venn-state';
import { ApplyFontDefaultsSection } from './components/ApplyFontDefaultsSection';
import type { DropResult } from '@hello-pangea/dnd';
import { cn } from '@/lib/utils';

const PAGE_DND_TYPE = 'PAGE';
const BLOCK_DND_TYPE = 'BLOCK';

const CONTINUATION_DROPPABLE_PREFIX = 'cont';

// The owner block id is part of the id because a block hosted on a continuation page grows its own continuation
// pages under the same page group, reusing the owner's continuation indices.
const makeContinuationDroppableId = ({
  sourceIndex,
  continuationIndex,
  sourceBlockId,
}: {
  sourceIndex: number;
  continuationIndex: number;
  sourceBlockId: BlockId;
}): string => [CONTINUATION_DROPPABLE_PREFIX, sourceIndex, continuationIndex, sourceBlockId].join(':');

const parseContinuationDroppableId = (
  droppableId: string,
): { sourceIndex: number; continuationIndex: number; sourceBlockId: BlockId } | undefined => {
  const [prefix, sourceIndexStr, continuationIndexStr, ...sourceBlockIdParts] = droppableId.split(':');
  if (prefix !== CONTINUATION_DROPPABLE_PREFIX) return undefined;
  const sourceIndex = parseInt(sourceIndexStr ?? '', 10);
  const continuationIndex = parseInt(continuationIndexStr ?? '', 10);
  if (Number.isNaN(sourceIndex) || Number.isNaN(continuationIndex)) return undefined;
  return { sourceIndex, continuationIndex, sourceBlockId: sourceBlockIdParts.join(':') };
};

const isDropRestrictedPageType = (type: PageType): boolean =>
  type === PageType.TITLE || type === PageType.SECTION || type === PageType.DISCLOSURE;

const ContractPageAction = ({ sourceBlockId }: { sourceBlockId: BlockId }) => {
  const { contract } = useExpandBlock(sourceBlockId);
  return <ItemActions onDelete={contract} customDeleteText="page (continuation)" />;
};

const isSourcePage = RenderedPage.$is('SourcePage');
const isContinuationPage = RenderedPage.$is('ContinuationPage');

const ContinuationBlockLabel = ({
  blockId,
  continuationIndex,
  totalContinuations,
  renderedIndex,
}: {
  blockId: BlockId;
  continuationIndex: number;
  totalContinuations: number;
  renderedIndex: number;
}) => {
  const header = useRecoilValue(blockDisplayHeader(blockId));
  const setting = useRecoilValue(blockSettings(blockId));
  const selected = useRecoilValue(selectedBlockIdState);
  const { onSelectPage } = useContext(StudioSidePanelContext);
  const pageNumber = continuationIndex + 2;
  const totalPages = totalContinuations + 1;
  return (
    <div
      {...buttonize(() => onSelectPage(renderedIndex, { selectBlockId: blockId }))}
      className="relative flex items-center gap-[5px] py-[5px] pl-[15px] pr-[20px] cursor-pointer hover:bg-venn-grey-10 hover:rounded-sm"
    >
      <div className="w-[15px] shrink-0" />
      {pageNumber !== totalPages && <LinkedBlockIcon />}
      <Icon type={getDynamicIcon(setting)} className="text-venn-black text-base shrink-0" />
      <div
        className={cn('flex items-baseline gap-x-1 min-w-0 flex-1 text-[14px]', selected === blockId && 'font-bold')}
      >
        <span className="truncate min-w-0">{header}</span>
        <span className="text-venn-grey-60 whitespace-nowrap shrink-0">
          ({pageNumber}/{totalPages})
        </span>
      </div>
    </div>
  );
};

const PagesPanel = () => {
  const { hasPermission } = useContext(UserContext);
  const { onDeletePage, onDuplicatePage } = useContext(StudioContext);
  const isReport = useRecoilValue(isReportState);
  const { onSelectPage } = useContext(StudioSidePanelContext);

  const selectedBlocks = useRecoilValue(selectedBlocksState);
  const pages = useRecoilValue(viewPages);
  const renderedPages = useRecoilValue(renderedPagesState);

  const orientation = useRecoilValue(studioPrintOrientationType);
  const noColumns = orientation === 'PORTRAIT' ? RL_GRID_PORTRAIT_NO_COLUMNS : RL_GRID_LANDSCAPE_NO_COLUMNS;
  const noRows = orientation === 'PORTRAIT' ? RL_GRID_PORTRAIT_NO_ROWS : RL_GRID_LANDSCAPE_NO_ROWS;

  const viewIdsOnPage: BlockId[][] = useMemo(
    () => pages.map((page) => sortLayoutByReadingOrder(page.layout).map((view) => view.i)),
    [pages],
  );

  const nonDraggableBlockIds = useRecoilValue(expandedBlockIdsState(viewIdsOnPage.flat()));

  const canReorder = hasPermission('STUDIO_REORDER_BLOCKS');

  const reactDnd = useModuleLazily(useCallback(() => import('@hello-pangea/dnd'), []));

  const pageGroups = useMemo(() => {
    const groups: {
      sourceIndex: number;
      page: (typeof renderedPages)[number] & { _tag: 'SourcePage' };
      renderedStartIndex: number;
      continuations: {
        sourceBlockId: BlockId;
        continuationIndex: number;
        totalContinuations: number;
        renderedIndex: number;
        sliceRowCount: number;
        blockLayoutW: number;
        userBlocks: Layout;
      }[];
    }[] = [];

    for (let i = 0; i < renderedPages.length; i++) {
      const rp = renderedPages[i]!;
      if (isSourcePage(rp)) {
        groups.push({ sourceIndex: rp.sourceIndex, page: rp, renderedStartIndex: i, continuations: [] });
      } else if (isContinuationPage(rp)) {
        groups[groups.length - 1]?.continuations.push({
          sourceBlockId: rp.sourceBlockId,
          continuationIndex: rp.continuationIndex,
          totalContinuations: rp.totalContinuations,
          renderedIndex: i,
          sliceRowCount: rp.sliceRowCount,
          blockLayoutW: rp.blockLayout.w,
          userBlocks: sortLayoutByReadingOrder(rp.userBlocks),
        });
      }
    }

    return groups;
  }, [renderedPages]);

  const onDragEnd = useRecoilCallback(
    ({ snapshot, set }) =>
      (result: DropResult) => {
        if (!result.destination) return;

        const { source, destination, type } = result;

        if (type === PAGE_DND_TYPE) {
          set(viewPages, (current) => {
            const newPages = [...current];
            const [moved] = newPages.splice(source.index, 1);
            if (!moved) return current;
            newPages.splice(destination.index, 0, moved);
            return newPages;
          });
          return;
        }

        if (type !== BLOCK_DND_TYPE) return;

        const isContinuationSource = !!parseContinuationDroppableId(source.droppableId);
        const isContinuationDrop = !!parseContinuationDroppableId(destination.droppableId);
        const findContinuation = (droppableId: string) => {
          const parsed = parseContinuationDroppableId(droppableId);
          if (!parsed) return undefined;
          return pageGroups
            .find((g) => g.sourceIndex === parsed.sourceIndex)
            ?.continuations.find(
              (c) => c.continuationIndex === parsed.continuationIndex && c.sourceBlockId === parsed.sourceBlockId,
            );
        };
        const currentPages = snapshot.getLoadable(viewPages).getValue();
        const clonePages = (ps: typeof currentPages) => ps.map((p) => ({ ...p, layout: [...p.layout] }));

        const resolveSource = ():
          | { moved: LayoutItem; sourcePageIdx: number; sourceSliceLayoutId: string | undefined }
          | undefined => {
          if (isContinuationSource) {
            const srcCont = findContinuation(source.droppableId);
            if (!srcCont) return undefined;
            const moved = srcCont.userBlocks[source.index];
            if (!moved) return undefined;
            return {
              moved,
              sourcePageIdx: NaN,
              sourceSliceLayoutId: makeContinuationLayoutId({
                sourceBlockId: srcCont.sourceBlockId,
                continuationIndex: srcCont.continuationIndex,
              }),
            };
          }

          const sourcePageIdx = parseInt(source.droppableId, 10);
          if (Number.isNaN(sourcePageIdx)) return undefined;
          const sourcePage = currentPages[sourcePageIdx];
          if (!sourcePage) return undefined;
          const moved = sortLayoutByReadingOrder(sourcePage.layout)[source.index];
          if (!moved) return undefined;
          return { moved, sourcePageIdx, sourceSliceLayoutId: undefined };
        };

        const resolvedSource = resolveSource();
        if (!resolvedSource) return;
        const { moved, sourcePageIdx, sourceSliceLayoutId } = resolvedSource;

        const destGroupIdx = isContinuationDrop
          ? (parseContinuationDroppableId(destination.droppableId)?.sourceIndex ?? NaN)
          : parseInt(destination.droppableId, 10);
        if (Number.isNaN(destGroupIdx)) return;
        const destPage = currentPages[destGroupIdx];
        if (!destPage) return;
        if (isDropRestrictedPageType(destPage.type)) return;

        if (!isContinuationSource && !isContinuationDrop && sourcePageIdx === destGroupIdx) {
          const expandedOnDest = destPage.layout.filter((l) => nonDraggableBlockIds.includes(l.i));
          if (expandedOnDest.length > 1) return;
          const expandedId = expandedOnDest[0]?.i;

          if (expandedId) {
            const expandedSourceLayout = snapshot.getLoadable(blockSourceLayoutState(expandedId)).getValue();
            if (!expandedSourceLayout) return;
            const maxUsableRows = snapshot.getLoadable(studioMaxUsableRows).getValue();
            const reordered = reorderAboveExpandedBlock({
              layout: destPage.layout,
              fromIndex: source.index,
              toIndex: destination.index,
              expandedBlockId: expandedId,
              noColumns,
              expandedMinRows: expandedSourceLayout.h,
              maxUsableRows,
            });
            if (!reordered) return;
            set(viewPages, (current) => {
              const newPages = clonePages(current);
              newPages[sourcePageIdx]!.layout = [...reordered];
              return newPages;
            });
            return;
          }

          set(viewPages, (current) => {
            const newPages = clonePages(current);
            const page = newPages[sourcePageIdx]!;
            page.layout = reorderBlockWithinPage({
              layout: page.layout,
              fromIndex: source.index,
              toIndex: destination.index,
              noColumns,
            });
            return newPages;
          });
          return;
        }

        if (isContinuationSource && isContinuationDrop && source.droppableId === destination.droppableId) {
          if (source.index === destination.index) return;
          const srcCont = findContinuation(source.droppableId);
          if (!srcCont || !sourceSliceLayoutId) return;
          const reordered = reorderContinuationUserBlocks({
            userBlocks: srcCont.userBlocks,
            fromIndex: source.index,
            toIndex: destination.index,
            noColumns,
            topOffset: srcCont.sliceRowCount,
            pageRows: noRows,
          });
          if (!reordered) return;
          set(continuationUserBlocksState(sourceSliceLayoutId), reordered);
          return;
        }

        const removeSource = () => {
          if (isContinuationSource) {
            set(continuationUserBlocksState(sourceSliceLayoutId!), (prev) => prev.filter((l) => l.i !== moved.i));
          } else {
            set(viewPages, (current) => {
              const newPages = clonePages(current);
              const src = newPages[sourcePageIdx]!;
              const srcHasExpanded = src.layout.some((l) => nonDraggableBlockIds.includes(l.i));
              const remaining = src.layout.filter((l) => l.i !== moved.i);
              src.layout = srcHasExpanded
                ? remaining
                : packLayoutByReadingOrder({ items: sortLayoutByReadingOrder(remaining), noColumns });
              return newPages;
            });
          }
        };

        const landInPage = (atIndex: number) => {
          if (isContinuationSource) {
            removeSource();
            set(viewPages, (current) => {
              const newPages = clonePages(current);
              newPages[destGroupIdx]!.layout = insertBlockIntoPage({
                layout: newPages[destGroupIdx]!.layout,
                item: moved,
                atIndex,
                noColumns,
              });
              return newPages;
            });
            return;
          }

          set(viewPages, (current) => {
            const newPages = clonePages(current);
            const src = newPages[sourcePageIdx]!;
            const srcHasExpanded = src.layout.some((l) => nonDraggableBlockIds.includes(l.i));
            const remaining = src.layout.filter((l) => l.i !== moved.i);
            src.layout = srcHasExpanded
              ? remaining
              : packLayoutByReadingOrder({ items: sortLayoutByReadingOrder(remaining), noColumns });
            newPages[destGroupIdx]!.layout = insertBlockIntoPage({
              layout: newPages[destGroupIdx]!.layout,
              item: moved,
              atIndex,
              noColumns,
            });
            return newPages;
          });
        };

        const landInContinuation = (destSliceLayoutId: string, userBlocks: Layout) => {
          removeSource();
          set(continuationUserBlocksState(destSliceLayoutId), userBlocks);
        };

        const destIds = viewIdsOnPage[destGroupIdx] ?? [];
        const expandedIdsOnDest = destIds.filter((id) => nonDraggableBlockIds.includes(id));

        if (!isContinuationDrop && expandedIdsOnDest.length === 0) {
          landInPage(destination.index);
          return;
        }

        if (expandedIdsOnDest.length > 1) return;
        const expandedId = expandedIdsOnDest[0];
        if (!expandedId) return;

        const classifyDestination = ():
          | {
              destinationKind: OutlineDropDestinationKind;
              isAbove: boolean;
              sliceLayoutId: string;
              existingUserBlocks: Layout;
              sliceRowCount: number;
            }
          | undefined => {
          const destGroup = pageGroups.find((g) => g.sourceIndex === destGroupIdx);

          if (!isContinuationDrop) {
            return {
              destinationKind: (destGroup?.continuations.length ?? 0) > 0 ? 'first-slice' : 'single-expanded',
              isAbove: destination.index <= destIds.indexOf(expandedId),
              sliceLayoutId: '',
              existingUserBlocks: [],
              sliceRowCount: 0,
            };
          }

          const cont = findContinuation(destination.droppableId);
          if (!cont) return undefined;

          const sliceLayoutId = makeContinuationLayoutId({
            sourceBlockId: cont.sourceBlockId,
            continuationIndex: cont.continuationIndex,
          });
          return {
            destinationKind: cont.continuationIndex + 1 < cont.totalContinuations ? 'middle' : 'last',
            isAbove: false,
            sliceLayoutId,
            existingUserBlocks: cont.userBlocks,
            sliceRowCount: cont.sliceRowCount,
          };
        };

        const destinationInfo = classifyDestination();
        if (!destinationInfo) return;
        const { destinationKind, isAbove, sliceLayoutId, existingUserBlocks, sliceRowCount } = destinationInfo;

        const expandedSourceLayout = snapshot.getLoadable(blockSourceLayoutState(expandedId)).getValue();
        if (!expandedSourceLayout) return;
        const maxUsableRows = snapshot.getLoadable(studioMaxUsableRows).getValue();
        const expandedRows = snapshot.getLoadable(blockExpandedRows(expandedId)).getValue();

        const plan = planOutlineExpandedDrop({
          destinationKind,
          isAbove,
          atIndex: destination.index,
          maxUsableRows,
          destLayout: destPage.layout,
          movedItem: moved,
          noColumns,
          expandedMinRows: expandedSourceLayout.h,
          expandedRows,
          expandedBlockId: expandedId,
          existingUserBlocks,
          sliceRowCount,
          noRows,
          sliceLayoutId,
        });

        Match.value(plan).pipe(
          Match.tag('Revert', () => {}),
          Match.tag('InsertIntoPage', ({ atIndex }) => landInPage(atIndex)),
          Match.tag('AppendToContinuation', ({ sliceLayoutId, userBlocks }) =>
            landInContinuation(sliceLayoutId, userBlocks),
          ),
          Match.exhaustive,
        );
      },
    [nonDraggableBlockIds, noColumns, noRows, pageGroups, viewIdsOnPage],
  );

  if (!isReport || !reactDnd) {
    return <div />;
  }

  const { DragDropContext, Droppable, Draggable } = reactDnd;

  return (
    <div>
      <ApplyFontDefaultsSection />
      <NewPageButton />
      <div>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="pages" type={PAGE_DND_TYPE}>
            {(pagesProvided) => (
              <div ref={pagesProvided.innerRef} {...pagesProvided.droppableProps}>
                {pageGroups.map((group) => {
                  const pageTitle = getPageTitle(group.page.page, group.renderedStartIndex);
                  const pageSelected = !!viewIdsOnPage[group.sourceIndex]?.some((id) => selectedBlocks.includes(id));
                  const hasContinuations = group.continuations.length > 0;
                  return (
                    <Draggable
                      key={`page-${group.sourceIndex}`}
                      draggableId={`page-${group.sourceIndex}`}
                      index={group.sourceIndex}
                      isDragDisabled={!canReorder}
                    >
                      {(pageProvided, pageSnapshot) => (
                        <div
                          ref={pageProvided.innerRef}
                          {...pageProvided.draggableProps}
                          className={cn(pageSnapshot.isDragging && 'bg-venn-grey-10 rounded shadow-md')}
                        >
                          <div
                            {...pageProvided.dragHandleProps}
                            {...buttonize(() => onSelectPage(group.renderedStartIndex))}
                            data-testid="scroll-to-page"
                            className={cn(
                              'relative flex items-center gap-[5px] h-[33px] cursor-pointer group/page',
                              'hover:bg-venn-grey-10 hover:rounded-sm justify-between',
                              '[&:not(:hover)_.reorderable-hover]:invisible',
                            )}
                          >
                            {hasContinuations && <PageIconContinuationLine isAfterIcon />}
                            <div className="flex items-center justify-center w-[15px] shrink-0">
                              {canReorder && (
                                <Icon
                                  type="arrows-up-down"
                                  className="reorderable-hover text-venn-deep-dark-blue text-[12px]"
                                />
                              )}
                            </div>
                            <div
                              className={cn(
                                'flex-1 whitespace-nowrap overflow-hidden text-ellipsis flex items-center text-[14px] flex-grow-[2]',
                                pageSelected && 'font-bold',
                              )}
                            >
                              <Icon type="file" className="relative z-[1] mr-[10px] shrink-0" />
                              {pageTitle}
                            </div>
                            <ItemActions
                              onDuplicate={
                                !hasPermission('STUDIO_INSERT_BLOCKS')
                                  ? undefined
                                  : () => onDuplicatePage(group.sourceIndex)
                              }
                              onDelete={
                                !hasPermission('STUDIO_DELETE_BLOCKS')
                                  ? undefined
                                  : () => onDeletePage(group.sourceIndex)
                              }
                              customDeleteText={`page (${pageTitle})`}
                            />
                            <div className="w-[3px] shrink-0" />
                          </div>

                          <Droppable
                            droppableId={group.sourceIndex.toString()}
                            type={BLOCK_DND_TYPE}
                            isDropDisabled={isDropRestrictedPageType(group.page.page.type)}
                          >
                            {(blocksProvided, blocksSnapshot) => (
                              <div
                                ref={blocksProvided.innerRef}
                                {...blocksProvided.droppableProps}
                                className={cn(
                                  'min-h-[4px] transition-colors duration-150',
                                  blocksSnapshot.isDraggingOver && 'bg-venn-grey-10 rounded-sm',
                                )}
                              >
                                {viewIdsOnPage[group.sourceIndex]!.map((blockId, blockIndex) => (
                                  <Draggable
                                    key={blockId}
                                    draggableId={blockId}
                                    index={blockIndex}
                                    isDragDisabled={!canReorder || nonDraggableBlockIds.includes(blockId)}
                                  >
                                    {(blockProvided, blockSnapshot) => (
                                      <div
                                        ref={blockProvided.innerRef}
                                        {...blockProvided.draggableProps}
                                        {...blockProvided.dragHandleProps}
                                        className={cn(
                                          '[&:not(:hover)_.reorderable-hover]:invisible',
                                          canReorder && 'hover:bg-venn-grey-10 hover:rounded-sm',
                                          blockSnapshot.isDragging && 'bg-venn-grey-10 rounded shadow-md',
                                        )}
                                      >
                                        <BlockItem
                                          id={blockId}
                                          showArrowOnHover={canReorder}
                                          showContinuationLine={hasContinuations}
                                        />
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                                {blocksProvided.placeholder}
                              </div>
                            )}
                          </Droppable>

                          {group.continuations.map((cont) => (
                            <div key={`continuation-${cont.sourceBlockId}-${cont.continuationIndex}`}>
                              <div
                                {...buttonize(() => onSelectPage(cont.renderedIndex))}
                                data-testid="scroll-to-page"
                                className={cn(
                                  'relative flex items-center gap-[5px] h-[33px] cursor-pointer',
                                  'hover:bg-venn-grey-10 hover:rounded-sm',
                                  '[&:not(:hover)_.reorderable-hover]:invisible',
                                )}
                              >
                                <PageIconContinuationLine isBeforeIcon />
                                {cont.continuationIndex !== cont.totalContinuations - 1 && (
                                  <PageIconContinuationLine isAfterIcon />
                                )}
                                <div className="w-[15px] shrink-0" />
                                <div className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis flex items-center text-[14px]">
                                  <Icon type="file" className="relative z-[1] mr-[10px] shrink-0" />
                                  Page {cont.renderedIndex + 1}
                                </div>
                                <ContractPageAction sourceBlockId={cont.sourceBlockId} />
                                <div className="w-[15px] shrink-0" />
                              </div>
                              <Droppable
                                droppableId={makeContinuationDroppableId({
                                  sourceIndex: group.sourceIndex,
                                  continuationIndex: cont.continuationIndex,
                                  sourceBlockId: cont.sourceBlockId,
                                })}
                                type={BLOCK_DND_TYPE}
                                isDropDisabled={cont.continuationIndex + 1 < cont.totalContinuations}
                              >
                                {(contBlocksProvided, contBlocksSnapshot) => (
                                  <div
                                    ref={contBlocksProvided.innerRef}
                                    {...contBlocksProvided.droppableProps}
                                    className={cn(
                                      'min-h-[4px] transition-colors duration-150',
                                      contBlocksSnapshot.isDraggingOver && 'bg-venn-grey-10 rounded-sm',
                                    )}
                                  >
                                    <ContinuationBlockLabel
                                      blockId={cont.sourceBlockId}
                                      continuationIndex={cont.continuationIndex}
                                      totalContinuations={cont.totalContinuations}
                                      renderedIndex={cont.renderedIndex}
                                    />
                                    {cont.userBlocks &&
                                      !!cont.userBlocks.length &&
                                      cont.continuationIndex + 1 === cont.totalContinuations &&
                                      cont.userBlocks.map((ub, userBlockIndex) => (
                                        <Draggable
                                          key={ub.i}
                                          draggableId={ub.i}
                                          index={userBlockIndex}
                                          isDragDisabled={!canReorder || nonDraggableBlockIds.includes(ub.i)}
                                        >
                                          {(blockProvided, blockSnapshot) => (
                                            <div
                                              ref={blockProvided.innerRef}
                                              {...blockProvided.draggableProps}
                                              {...blockProvided.dragHandleProps}
                                              className={cn(
                                                '[&:not(:hover)_.reorderable-hover]:invisible',
                                                canReorder && 'hover:bg-venn-grey-10 hover:rounded-sm',
                                                blockSnapshot.isDragging && 'bg-venn-grey-10 rounded shadow-md',
                                              )}
                                            >
                                              <BlockItem id={ub.i} showArrowOnHover={canReorder} />
                                            </div>
                                          )}
                                        </Draggable>
                                      ))}
                                    {contBlocksProvided.placeholder}
                                  </div>
                                )}
                              </Droppable>
                            </div>
                          ))}
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {pagesProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </div>
  );
};

export default PagesPanel;
